import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Toggle - Button-style toggle component
 * Useful for on/off states with button appearance
 */
const Toggle = ({ 
  pressed = false, 
  onPressedChange, 
  variant = 'default', // 'default', 'outline', 'ghost'
  size = 'default', // 'sm', 'default', 'lg'
  disabled = false,
  children,
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  const handleClick = () => {
    if (disabled) return;
    onPressedChange && onPressedChange(!pressed);
  };

  // Size variants
  const sizeClasses = {
    sm: 'h-8 px-2.5 text-xs',
    default: 'h-10 px-3 text-sm',
    lg: 'h-11 px-5 text-base'
  };

  // Variant styles
  const getVariantClasses = () => {
    const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';
    
    if (disabled) {
      return `${baseClasses} opacity-50 cursor-not-allowed pointer-events-none`;
    }

    switch (variant) {
      case 'outline':
        return `${baseClasses} border border-gray-300 dark:border-gray-600 ${
          pressed 
            ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600' 
            : `${theme.colors.surface} ${theme.text.primary} hover:bg-gray-50 dark:hover:bg-gray-800`
        }`;
      
      case 'ghost':
        return `${baseClasses} ${
          pressed 
            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
            : `${theme.text.primary} hover:bg-gray-100 dark:hover:bg-gray-800`
        }`;
      
      default: // 'default'
        return `${baseClasses} ${
          pressed 
            ? 'bg-blue-600 dark:bg-blue-500 text-white shadow-md' 
            : `${theme.colors.surface} ${theme.text.primary} hover:bg-gray-100 dark:hover:bg-gray-700`
        }`;
    }
  };

  return (
    <button
      type="button"
      role="button"
      aria-pressed={pressed}
      onClick={handleClick}
      disabled={disabled}
      className={`
        ${getVariantClasses()}
        ${sizeClasses[size] || sizeClasses.default}
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * ToggleGroup - Group of toggle buttons (single or multiple selection)
 */
const ToggleGroup = ({ 
  type = 'single', // 'single' or 'multiple'
  value, 
  onValueChange, 
  children, 
  orientation = 'horizontal', // 'horizontal' or 'vertical'
  className = '',
  ...props 
}) => {
  const handleToggle = (toggleValue) => {
    if (type === 'single') {
      onValueChange && onValueChange(value === toggleValue ? null : toggleValue);
    } else {
      // Multiple selection
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(toggleValue)
        ? currentValues.filter(v => v !== toggleValue)
        : [...currentValues, toggleValue];
      onValueChange && onValueChange(newValues);
    }
  };

  const containerClass = `
    inline-flex gap-1
    ${orientation === 'vertical' ? 'flex-col' : 'flex-row'}
    ${className}
  `.trim();

  return (
    <div className={containerClass} role="group" {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          const childValue = child.props.value;
          const isPressed = type === 'single' 
            ? value === childValue 
            : Array.isArray(value) && value.includes(childValue);
          
          return React.cloneElement(child, {
            pressed: isPressed,
            onPressedChange: () => handleToggle(childValue),
          });
        }
        return child;
      })}
    </div>
  );
};

export { Toggle, ToggleGroup };
export default Toggle;
